<script>
import Layout from "../../layouts/main";
import PageHeader from "@/components/page-header";
import draggable from "vuedraggable";
import {
  createBlock,
  defaultBlocks,
  generateContent,
  blocksToPreviewLines,
  normalizeBlocks,
  presetMeituan,
  presetFlashBuy,
  replaceVars,
  SAMPLE_DATA,
  TEMPLATE_VARS,
  FONT_TAGS,
  lineBytes,
} from "@/utils/yilianyunPrint";

const BLOCK_TYPES = [
  "shopinfo",
  "member",
  "payway",
  "title",
  "text",
  "separator",
  "blank",
  "lr",
  "table",
  "qr",
  "barcode",
];

/**
 * 易联云自定义打印模板排版工具
 */
export default {
  page() {
    return {
      title: this.title,
      meta: [{ name: "description" }],
    };
  },
  components: { Layout, PageHeader, draggable },
  data() {
    return {
      title: "",
      items: [],
      userinfo: null,
      loading: false,
      saveLoading: false,
      testPrintLoading: false,
      paperWidth: 58,
      copies: 1,
      blocks: defaultBlocks(),
      selectedId: null,
      showContent: false,
      blockTypes: BLOCK_TYPES,
      fontTags: FONT_TAGS,
      templateVars: TEMPLATE_VARS,
      dragOptions: {
        animation: 180,
        handle: ".block-drag-handle",
        ghostClass: "block-ghost",
        chosenClass: "block-chosen",
        dragClass: "block-drag",
        forceFallback: true,
        fallbackClass: "block-drag-fallback",
      },
    };
  },
  computed: {
    selectedBlock() {
      if (!this.selectedId) return null;
      return this.blocks.find((b) => b.id === this.selectedId) || null;
    },
    selectedIndex() {
      if (!this.selectedId) return -1;
      return this.blocks.findIndex((b) => b.id === this.selectedId);
    },
    content() {
      return generateContent(this.blocks, {
        paperWidth: this.paperWidth,
        copies: this.copies,
      });
    },
    /** 测试打印用：占位变量替换为示例数据 */
    printContent() {
      return replaceVars(this.content, SAMPLE_DATA);
    },
    previewLines() {
      return blocksToPreviewLines(this.blocks, this.paperWidth);
    },
    ticketWidthPx() {
      // 约 8px/字符宽度视觉（按字节）
      return lineBytes(this.paperWidth) * 8 + 32;
    },
  },
  mounted() {
    this.title = this.$t("clouds.printer.editor.title");
    this.items = [
      { text: this.$t("menuitems.clouds.text"), href: "/" },
      {
        text: this.$t("menuitems.clouds.printer.text"),
        href: "/clouds/printer",
      },
      {
        text: this.$t("clouds.printer.editor.title"),
        active: true,
      },
    ];

    try {
      this.userinfo = JSON.parse(localStorage.getItem("user"));
      if (!this.userinfo) {
        this.$message.error(this.$t("global.errors.loginrequired"));
        this.$router.push({ name: "login" });
        return;
      }
    } catch (e) {
      this.$message.error(this.$t("global.errors.loginrequired"));
      this.$router.push({ name: "login" });
      return;
    }

    if (this.blocks.length) {
      this.selectedId = this.blocks[0].id;
    }
    this.fetchTemplate();
  },
  methods: {
    blockLabel(type) {
      return this.$t(`clouds.printer.editor.blocks.${type}`);
    },
    shopinfoSummary(block) {
      const parts = [];
      if (block.showName !== false) parts.push(this.$t("clouds.printer.editor.shop_name"));
      if (block.showAddress) parts.push(this.$t("clouds.printer.editor.shop_address"));
      if (block.showPhone) parts.push(this.$t("clouds.printer.editor.shop_phone"));
      return parts.length ? parts.join(" · ") : "-";
    },
    memberSummary(block) {
      const parts = [];
      if (block.showCardno) parts.push(this.$t("clouds.printer.editor.member_cardno"));
      if (block.showMemberName) parts.push(this.$t("clouds.printer.editor.member_name"));
      if (block.showMemberUsername) parts.push(this.$t("clouds.printer.editor.member_username"));
      if (block.showMemberPhone) parts.push(this.$t("clouds.printer.editor.member_phone"));
      if (block.showMemberAmount) parts.push(this.$t("clouds.printer.editor.member_amount"));
      if (block.showMemberHex) parts.push(this.$t("clouds.printer.editor.member_hex"));
      return parts.length ? parts.join(" · ") : this.$t("clouds.printer.editor.member_cardno");
    },
    selectBlock(id) {
      this.selectedId = id;
    },
    addBlock(type) {
      const block = createBlock(type);
      const idx = this.selectedIndex;
      if (idx >= 0) {
        this.blocks.splice(idx + 1, 0, block);
      } else {
        this.blocks.push(block);
      }
      this.selectedId = block.id;
    },
    removeBlock(id) {
      const idx = this.blocks.findIndex((b) => b.id === id);
      if (idx < 0) return;
      this.blocks.splice(idx, 1);
      if (this.selectedId === id) {
        const next = this.blocks[idx] || this.blocks[idx - 1] || null;
        this.selectedId = next ? next.id : null;
      }
    },
    moveBlock(id, dir) {
      const idx = this.blocks.findIndex((b) => b.id === id);
      if (idx < 0) return;
      const target = idx + dir;
      if (target < 0 || target >= this.blocks.length) return;
      const item = this.blocks.splice(idx, 1)[0];
      this.blocks.splice(target, 0, item);
    },
    onDragStart() {
      document.body.classList.add("printer-block-dragging");
    },
    onDragEnd(evt) {
      document.body.classList.remove("printer-block-dragging");
      const item = this.blocks[evt.newIndex];
      if (item) this.selectedId = item.id;
    },
    insertVar(key) {
      const b = this.selectedBlock;
      if (!b) return;
      // 与后台 orderprint / yilianyun.txt 占位符一致：{ordertime}
      const token = `{${key}}`;
      if (b.type === "lr") {
        b.left = (b.left || "") + token;
      } else if (b.type === "qr" || b.type === "barcode") {
        b.value = (b.value || "") + token;
      } else if (b.type === "table") {
        // ignore
      } else {
        b.text = (b.text || "") + token;
      }
    },
    addTableRow() {
      const b = this.selectedBlock;
      if (!b || b.type !== "table") return;
      if (!Array.isArray(b.rows)) b.rows = [];
      b.rows.push({ name: "", qty: "x1", price: "￥0" });
    },
    removeTableRow(i) {
      const b = this.selectedBlock;
      if (!b || b.type !== "table" || !Array.isArray(b.rows)) return;
      b.rows.splice(i, 1);
    },
    loadPreset(name) {
      this.$confirm(
        this.$t("clouds.printer.editor.preset_confirm"),
        this.$t("clouds.printer.editor.preset"),
        { type: "warning" }
      )
        .then(() => {
          if (name === "meituan") {
            this.blocks = presetMeituan();
          } else {
            this.blocks = presetFlashBuy();
          }
          this.selectedId = this.blocks[0] ? this.blocks[0].id : null;
          this.$message.success(this.$t("clouds.printer.editor.preset_loaded"));
        })
        .catch(() => {});
    },
    resetDefault() {
      this.$confirm(
        this.$t("clouds.printer.editor.reset_confirm"),
        this.$t("clouds.printer.editor.reset"),
        { type: "warning" }
      )
        .then(() => {
          this.blocks = defaultBlocks();
          this.selectedId = this.blocks[0] ? this.blocks[0].id : null;
        })
        .catch(() => {});
    },
    copyContent() {
      const text = this.content;
      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(text).then(
          () => {
            this.$message.success(this.$t("clouds.printer.editor.copy_success"));
          },
          () => this.fallbackCopy(text)
        );
      } else {
        this.fallbackCopy(text);
      }
    },
    fallbackCopy(text) {
      const ta = document.createElement("textarea");
      ta.value = text;
      ta.style.position = "fixed";
      ta.style.left = "-9999px";
      document.body.appendChild(ta);
      ta.select();
      try {
        document.execCommand("copy");
        this.$message.success(this.$t("clouds.printer.editor.copy_success"));
      } catch (e) {
        this.$message.error(this.$t("clouds.printer.editor.copy_fail"));
      }
      document.body.removeChild(ta);
    },
    fetchTemplate() {
      this.loading = true;
      this.$axios
        .post(
          this.apiuri,
          { action: "get_print_template" },
          { headers: { Openid: this.userinfo.openid } }
        )
        .then((response) => {
          this.loading = false;
          if (response.data && response.data.status === 200) {
            const d = response.data;
            if (d.paperWidth === 58 || d.paperWidth === 72) {
              this.paperWidth = d.paperWidth;
            }
            if (d.copies != null) {
              this.copies = Math.min(9, Math.max(1, Number(d.copies) || 1));
            }
            if (Array.isArray(d.blocks) && d.blocks.length) {
              this.blocks = normalizeBlocks(d.blocks);
              this.selectedId = this.blocks[0] ? this.blocks[0].id : null;
            }
          }
          // 接口未就绪或无数据时保留默认模板
        })
        .catch(() => {
          this.loading = false;
        });
    },
    saveTemplate() {
      this.saveLoading = true;
      const payload = {
        action: "save_print_template",
        paperWidth: this.paperWidth,
        copies: this.copies,
        blocks: this.blocks,
        content: this.content,
      };
      this.$axios
        .post(this.apiuri, payload, {
          headers: { Openid: this.userinfo.openid },
        })
        .then((response) => {
          this.saveLoading = false;
          if (response.data && response.data.status === 200) {
            this.$message.success(
              response.data.msg || this.$t("global.success")
            );
          } else {
            this.$message.error(
              (response.data && (response.data.msg || response.data.message)) ||
                this.$t("global.errors.operation")
            );
          }
        })
        .catch(() => {
          this.saveLoading = false;
          this.$message.error(this.$t("global.errors.network"));
        });
    },
    testPrint() {
      if (!this.printContent || !String(this.printContent).trim()) {
        this.$message.warning(this.$t("clouds.printer.editor.test_print_empty"));
        return;
      }
      this.testPrintLoading = true;
      this.$axios
        .post(
          this.apiuri,
          {
            action: "test_print_template",
            paperWidth: this.paperWidth,
            copies: this.copies,
            content: this.printContent,
          },
          { headers: { Openid: this.userinfo.openid } }
        )
        .then((response) => {
          this.testPrintLoading = false;
          if (response.data && response.data.status === 200) {
            this.$message.success(
              response.data.msg ||
                this.$t("clouds.printer.editor.test_print_success")
            );
          } else {
            this.$message.error(
              (response.data && (response.data.msg || response.data.message)) ||
                this.$t("clouds.printer.editor.test_print_fail")
            );
          }
        })
        .catch(() => {
          this.testPrintLoading = false;
          this.$message.error(this.$t("global.errors.network"));
        });
    },
    lineClass(line) {
      return {
        "ticket-line": true,
        "is-bold": line.bold,
        "is-large": line.large,
        "is-fs2": line.fs2,
        "is-qr": line.type === "qr",
        "is-barcode": line.type === "barcode",
      };
    },
  },
};
</script>

<template>
  <Layout>
    <PageHeader :title="title" :items="items" />
    <div class="row printer-editor" v-loading="loading">
      <!-- 工具栏 -->
      <div class="col-12 mb-3">
        <el-card shadow="never" class="toolbar-card">
          <div class="toolbar">
            <div class="toolbar-group">
              <span class="toolbar-label">{{ $t("clouds.printer.editor.paper_width") }}</span>
              <el-radio-group v-model="paperWidth" size="small">
                <el-radio-button :label="58">58mm</el-radio-button>
                <el-radio-button :label="72">72mm</el-radio-button>
              </el-radio-group>
            </div>
            <div class="toolbar-group">
              <span class="toolbar-label">{{ $t("clouds.printer.editor.copies") }}</span>
              <el-input-number
                v-model="copies"
                :min="1"
                :max="9"
                size="small"
                controls-position="right"
              />
            </div>
            <div class="toolbar-group">
              <el-dropdown trigger="click" @command="loadPreset">
                <el-button size="small">
                  {{ $t("clouds.printer.editor.preset") }}
                  <i class="el-icon-arrow-down el-icon--right"></i>
                </el-button>
                <el-dropdown-menu slot="dropdown">
                  <el-dropdown-item command="flash">
                    {{ $t("clouds.printer.editor.preset_flash") }}
                  </el-dropdown-item>
                  <el-dropdown-item command="meituan">
                    {{ $t("clouds.printer.editor.preset_meituan") }}
                  </el-dropdown-item>
                </el-dropdown-menu>
              </el-dropdown>
              <el-button size="small" @click="resetDefault">
                {{ $t("clouds.printer.editor.reset") }}
              </el-button>
            </div>
            <div class="toolbar-group toolbar-actions">
              <el-button size="small" icon="el-icon-document-copy" @click="copyContent">
                {{ $t("clouds.printer.editor.copy") }}
              </el-button>
              <el-button
                type="warning"
                size="small"
                icon="el-icon-printer"
                :loading="testPrintLoading"
                @click="testPrint"
              >
                {{ $t("clouds.printer.editor.test_print") }}
              </el-button>
              <el-button
                type="primary"
                size="small"
                icon="el-icon-check"
                :loading="saveLoading"
                @click="saveTemplate"
              >
                {{ $t("clouds.printer.editor.save") }}
              </el-button>
              <el-button size="small" @click="$router.push({ name: 'Cloud Printer' })">
                {{ $t("global.button.back") }}
              </el-button>
            </div>
          </div>
        </el-card>
      </div>

      <!-- 块列表 -->
      <div class="col-lg-4 col-md-12 mb-3">
        <el-card shadow="never" class="panel-card">
          <div slot="header" class="clearfix block-list-header">
            <span>
              <i class="el-icon-menu"></i>
              {{ $t("clouds.printer.editor.block_list") }}
            </span>
            <small class="drag-tip text-muted">{{ $t("clouds.printer.editor.drag_tip") }}</small>
          </div>
          <div class="add-block-bar">
            <el-button
              v-for="t in blockTypes"
              :key="t"
              size="mini"
              plain
              @click="addBlock(t)"
            >
              {{ blockLabel(t) }}
            </el-button>
          </div>
          <draggable
            v-model="blocks"
            class="block-list"
            v-bind="dragOptions"
            @start="onDragStart"
            @end="onDragEnd"
          >
            <div
              v-for="(block, index) in blocks"
              :key="block.id"
              class="block-item"
              :class="{ active: block.id === selectedId }"
              @click="selectBlock(block.id)"
            >
              <div
                class="block-drag-handle"
                :title="$t('clouds.printer.editor.drag_handle')"
                @click.stop
              >
                <i class="el-icon-rank"></i>
              </div>
              <div class="block-item-main">
                <span class="block-index">{{ index + 1 }}</span>
                <span class="block-type">{{ blockLabel(block.type) }}</span>
                <span class="block-summary text-muted">
                  <template v-if="block.type === 'separator'">{{ block.char }}{{ block.char }}{{ block.char }}</template>
                  <template v-else-if="block.type === 'blank'">↵</template>
                  <template v-else-if="block.type === 'lr'">{{ block.left }} / {{ block.right }}</template>
                  <template v-else-if="block.type === 'table'">{{ (block.rows || []).length }} rows</template>
                  <template v-else-if="block.type === 'qr' || block.type === 'barcode'">{{ block.value }}</template>
                  <template v-else-if="block.type === 'shopinfo'">{{ shopinfoSummary(block) }}</template>
                  <template v-else-if="block.type === 'member'">{{ memberSummary(block) }}</template>
                  <template v-else-if="block.type === 'payway'">{{ block.payLabel }} / {{ block.payValue }}</template>
                  <template v-else>{{ block.text }}</template>
                </span>
              </div>
              <div class="block-item-actions" @click.stop>
                <el-button
                  type="text"
                  icon="el-icon-top"
                  :disabled="index === 0"
                  @click="moveBlock(block.id, -1)"
                />
                <el-button
                  type="text"
                  icon="el-icon-bottom"
                  :disabled="index === blocks.length - 1"
                  @click="moveBlock(block.id, 1)"
                />
                <el-button
                  type="text"
                  icon="el-icon-delete"
                  class="danger-text"
                  @click="removeBlock(block.id)"
                />
              </div>
            </div>
          </draggable>
          <div v-if="!blocks.length" class="empty-tip text-muted">
            {{ $t("clouds.printer.editor.empty_blocks") }}
          </div>
        </el-card>
      </div>

      <!-- 属性编辑 -->
      <div class="col-lg-4 col-md-12 mb-3">
        <el-card shadow="never" class="panel-card">
          <div slot="header" class="clearfix">
            <span>
              <i class="el-icon-edit-outline"></i>
              {{ $t("clouds.printer.editor.props") }}
            </span>
          </div>
          <div v-if="!selectedBlock" class="empty-tip text-muted">
            {{ $t("clouds.printer.editor.select_block") }}
          </div>
          <el-form v-else label-width="88px" label-position="left" size="small">
            <el-form-item :label="$t('clouds.printer.editor.block_type')">
              <el-tag size="small">{{ blockLabel(selectedBlock.type) }}</el-tag>
            </el-form-item>

            <template v-if="selectedBlock.type === 'text' || selectedBlock.type === 'title'">
              <el-form-item :label="$t('clouds.printer.editor.text')">
                <el-input
                  v-model="selectedBlock.text"
                  type="textarea"
                  :rows="3"
                  :placeholder="$t('clouds.printer.editor.text_placeholder')"
                />
              </el-form-item>
              <el-form-item :label="$t('clouds.printer.editor.align')">
                <el-radio-group v-model="selectedBlock.align">
                  <el-radio-button label="left">{{ $t("clouds.printer.editor.align_left") }}</el-radio-button>
                  <el-radio-button label="center">{{ $t("clouds.printer.editor.align_center") }}</el-radio-button>
                  <el-radio-button label="right">{{ $t("clouds.printer.editor.align_right") }}</el-radio-button>
                </el-radio-group>
              </el-form-item>
              <el-form-item :label="$t('clouds.printer.editor.font')">
                <el-select v-model="selectedBlock.font" style="width: 100%">
                  <el-option
                    v-for="f in fontTags"
                    :key="f"
                    :label="f === 'normal' ? $t('clouds.printer.editor.font_normal') : f"
                    :value="f"
                  />
                </el-select>
              </el-form-item>
            </template>

            <template v-else-if="selectedBlock.type === 'separator'">
              <el-form-item :label="$t('clouds.printer.editor.sep_char')">
                <el-radio-group v-model="selectedBlock.char">
                  <el-radio-button label="-">-</el-radio-button>
                  <el-radio-button label="*">*</el-radio-button>
                  <el-radio-button label="=">=</el-radio-button>
                </el-radio-group>
              </el-form-item>
            </template>

            <template v-else-if="selectedBlock.type === 'blank'">
              <p class="text-muted tip-text">{{ $t("clouds.printer.editor.blank_tip") }}</p>
            </template>

            <template v-else-if="selectedBlock.type === 'lr'">
              <el-form-item :label="$t('clouds.printer.editor.left_text')">
                <el-input v-model="selectedBlock.left" />
              </el-form-item>
              <el-form-item :label="$t('clouds.printer.editor.right_text')">
                <el-input v-model="selectedBlock.right" />
              </el-form-item>
            </template>

            <template v-else-if="selectedBlock.type === 'table'">
              <el-form-item :label="$t('clouds.printer.editor.font')">
                <el-select v-model="selectedBlock.font" style="width: 100%">
                  <el-option
                    v-for="f in fontTags"
                    :key="f"
                    :label="f === 'normal' ? $t('clouds.printer.editor.font_normal') : f"
                    :value="f"
                  />
                </el-select>
              </el-form-item>
              <div class="table-rows">
                <div
                  v-for="(row, ri) in selectedBlock.rows"
                  :key="ri"
                  class="table-row-edit"
                >
                  <el-input v-model="row.name" size="mini" :placeholder="$t('clouds.printer.editor.col_name')" />
                  <el-input v-model="row.qty" size="mini" :placeholder="$t('clouds.printer.editor.col_qty')" />
                  <el-input v-model="row.price" size="mini" :placeholder="$t('clouds.printer.editor.col_price')" />
                  <el-button type="text" icon="el-icon-delete" @click="removeTableRow(ri)" />
                </div>
                <el-button size="mini" icon="el-icon-plus" @click="addTableRow">
                  {{ $t("clouds.printer.editor.add_row") }}
                </el-button>
              </div>
              <p class="text-muted tip-text">{{ $t("clouds.printer.editor.table_tip") }}</p>
            </template>

            <template v-else-if="selectedBlock.type === 'qr'">
              <el-form-item :label="$t('clouds.printer.editor.code_value')">
                <el-input v-model="selectedBlock.value" />
              </el-form-item>
            </template>

            <template v-else-if="selectedBlock.type === 'barcode'">
              <el-form-item :label="$t('clouds.printer.editor.code_value')">
                <el-input v-model="selectedBlock.value" />
              </el-form-item>
              <el-form-item :label="$t('clouds.printer.editor.barcode_type')">
                <el-select v-model="selectedBlock.barcodeType" style="width: 100%">
                  <el-option label="EAN13 (BR)" value="BR" />
                  <el-option label="Code128B (BR2)" value="BR2" />
                </el-select>
              </el-form-item>
            </template>

            <template v-else-if="selectedBlock.type === 'shopinfo'">
              <el-form-item :label="$t('clouds.printer.editor.shop_fields')">
                <el-checkbox v-model="selectedBlock.showName">{{ $t("clouds.printer.editor.shop_name") }}</el-checkbox>
                <el-checkbox v-model="selectedBlock.showAddress">{{ $t("clouds.printer.editor.shop_address") }}</el-checkbox>
                <el-checkbox v-model="selectedBlock.showPhone">{{ $t("clouds.printer.editor.shop_phone") }}</el-checkbox>
              </el-form-item>
              <template v-if="selectedBlock.showName !== false">
                <el-form-item :label="$t('clouds.printer.editor.shop_name_text')">
                  <el-input v-model="selectedBlock.nameText" :placeholder="$t('clouds.printer.editor.shop_name_placeholder')" />
                </el-form-item>
                <el-form-item :label="$t('clouds.printer.editor.align')">
                  <el-radio-group v-model="selectedBlock.nameAlign">
                    <el-radio-button label="left">{{ $t("clouds.printer.editor.align_left") }}</el-radio-button>
                    <el-radio-button label="center">{{ $t("clouds.printer.editor.align_center") }}</el-radio-button>
                    <el-radio-button label="right">{{ $t("clouds.printer.editor.align_right") }}</el-radio-button>
                  </el-radio-group>
                </el-form-item>
                <el-form-item :label="$t('clouds.printer.editor.shop_name_font')">
                  <el-select v-model="selectedBlock.nameFont" style="width: 100%">
                    <el-option
                      v-for="f in fontTags"
                      :key="f"
                      :label="f === 'normal' ? $t('clouds.printer.editor.font_normal') : f"
                      :value="f"
                    />
                  </el-select>
                </el-form-item>
              </template>
              <template v-if="selectedBlock.showAddress || selectedBlock.showPhone">
                <el-form-item :label="$t('clouds.printer.editor.shop_contact_font')">
                  <el-select v-model="selectedBlock.contactFont" style="width: 100%">
                    <el-option
                      v-for="f in fontTags"
                      :key="'c'+f"
                      :label="f === 'normal' ? $t('clouds.printer.editor.font_normal') : f"
                      :value="f"
                    />
                  </el-select>
                </el-form-item>
              </template>
              <p class="text-muted tip-text">{{ $t("clouds.printer.editor.shopinfo_tip") }}</p>
            </template>

            <template v-else-if="selectedBlock.type === 'payway'">
              <el-form-item :label="$t('clouds.printer.editor.pay_layout')">
                <el-radio-group v-model="selectedBlock.payLayout">
                  <el-radio-button label="table">{{ $t("clouds.printer.editor.pay_layout_table") }}</el-radio-button>
                  <el-radio-button label="lr">{{ $t("clouds.printer.editor.pay_layout_lr") }}</el-radio-button>
                  <el-radio-button label="text">{{ $t("clouds.printer.editor.pay_layout_text") }}</el-radio-button>
                </el-radio-group>
              </el-form-item>
              <el-form-item :label="$t('clouds.printer.editor.pay_label')">
                <el-input v-model="selectedBlock.payLabel" />
              </el-form-item>
              <el-form-item :label="$t('clouds.printer.editor.pay_value')">
                <el-input v-model="selectedBlock.payValue" :placeholder="'{paytype}'" />
              </el-form-item>
              <el-form-item :label="$t('clouds.printer.editor.font')">
                <el-select v-model="selectedBlock.font" style="width: 100%">
                  <el-option
                    v-for="f in fontTags"
                    :key="f"
                    :label="f === 'normal' ? $t('clouds.printer.editor.font_normal') : f"
                    :value="f"
                  />
                </el-select>
              </el-form-item>
              <p class="text-muted tip-text">{{ $t("clouds.printer.editor.payway_tip") }}</p>
            </template>

            <template v-else-if="selectedBlock.type === 'member'">
              <el-form-item :label="$t('clouds.printer.editor.member_fields')">
                <el-checkbox v-model="selectedBlock.showCardno">{{ $t("clouds.printer.editor.member_cardno") }}</el-checkbox>
                <el-checkbox v-model="selectedBlock.showMemberName">{{ $t("clouds.printer.editor.member_name") }}</el-checkbox>
                <el-checkbox v-model="selectedBlock.showMemberUsername">{{ $t("clouds.printer.editor.member_username") }}</el-checkbox>
                <el-checkbox v-model="selectedBlock.showMemberPhone">{{ $t("clouds.printer.editor.member_phone") }}</el-checkbox>
                <el-checkbox v-model="selectedBlock.showMemberAmount">{{ $t("clouds.printer.editor.member_amount") }}</el-checkbox>
                <el-checkbox v-model="selectedBlock.showMemberHex">{{ $t("clouds.printer.editor.member_hex") }}</el-checkbox>
              </el-form-item>
              <el-form-item :label="$t('clouds.printer.editor.pay_layout')">
                <el-radio-group v-model="selectedBlock.memberLayout">
                  <el-radio-button label="text">{{ $t("clouds.printer.editor.pay_layout_text") }}</el-radio-button>
                  <el-radio-button label="lr">{{ $t("clouds.printer.editor.pay_layout_lr") }}</el-radio-button>
                  <el-radio-button label="table">{{ $t("clouds.printer.editor.pay_layout_table") }}</el-radio-button>
                </el-radio-group>
              </el-form-item>
              <el-form-item :label="$t('clouds.printer.editor.font')">
                <el-select v-model="selectedBlock.font" style="width: 100%">
                  <el-option
                    v-for="f in fontTags"
                    :key="'m'+f"
                    :label="f === 'normal' ? $t('clouds.printer.editor.font_normal') : f"
                    :value="f"
                  />
                </el-select>
              </el-form-item>
              <p class="text-muted tip-text">{{ $t("clouds.printer.editor.member_tip") }}</p>
            </template>

            <el-form-item
              v-if="selectedBlock.type !== 'blank' && selectedBlock.type !== 'separator' && selectedBlock.type !== 'table' && selectedBlock.type !== 'shopinfo' && selectedBlock.type !== 'member'"
              :label="$t('clouds.printer.editor.variables')"
            >
              <div class="var-chips">
                <el-tag
                  v-for="v in templateVars"
                  :key="v"
                  size="mini"
                  class="var-chip"
                  effect="plain"
                  @click="insertVar(v)"
                >
                  {{ v }}
                </el-tag>
              </div>
            </el-form-item>
          </el-form>
        </el-card>
      </div>

      <!-- 预览 -->
      <div class="col-lg-4 col-md-12 mb-3">
        <el-card shadow="never" class="panel-card">
          <div slot="header" class="clearfix">
            <span>
              <i class="el-icon-view"></i>
              {{ $t("clouds.printer.editor.preview") }}
            </span>
            <el-button
              style="float: right; padding: 3px 0"
              type="text"
              @click="showContent = !showContent"
            >
              {{ showContent ? $t("clouds.printer.editor.hide_content") : $t("clouds.printer.editor.show_content") }}
            </el-button>
          </div>
          <div class="ticket-wrap">
            <div
              class="ticket"
              :style="{ width: ticketWidthPx + 'px' }"
            >
              <div
                v-for="(line, li) in previewLines"
                :key="li"
                :class="lineClass(line)"
              >
                <template v-if="line.type === 'qr'">
                  <div class="qr-box">{{ line.text }}</div>
                </template>
                <template v-else-if="line.type === 'barcode'">
                  <div class="barcode-box">{{ line.text }}</div>
                </template>
                <template v-else>
                  <pre class="ticket-pre">{{ line.text || " " }}</pre>
                </template>
              </div>
            </div>
          </div>
          <div v-if="showContent" class="content-panel">
            <div class="content-label">{{ $t("clouds.printer.editor.content") }}</div>
            <el-input
              type="textarea"
              :value="content"
              :rows="10"
              readonly
              class="content-textarea"
            />
          </div>
        </el-card>
      </div>
    </div>
  </Layout>
</template>

<style scoped>
.toolbar {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 16px;
}
.toolbar-group {
  display: flex;
  align-items: center;
  gap: 8px;
}
.toolbar-label {
  font-size: 13px;
  color: #606266;
  white-space: nowrap;
}
.toolbar-actions {
  margin-left: auto;
}
.panel-card {
  min-height: 420px;
}
.clearfix i {
  margin-right: 5px;
}
.block-list-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
}
.block-list-header .drag-tip {
  margin: 0;
  font-weight: normal;
  white-space: nowrap;
}
.add-block-bar {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-bottom: 12px;
}
.block-list {
  max-height: 520px;
  overflow-y: auto;
  min-height: 40px;
}
.block-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 10px;
  border: 1px solid #ebeef5;
  border-radius: 4px;
  margin-bottom: 6px;
  cursor: pointer;
  background: #fff;
  transition: border-color 0.15s, background 0.15s, box-shadow 0.15s;
}
.block-item:hover {
  border-color: #c0c4cc;
}
.block-item.active {
  border-color: #409eff;
  background: #ecf5ff;
}
.block-item-main {
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
  flex: 1;
}
.block-drag-handle {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 28px;
  margin-right: 2px;
  color: #909399;
  cursor: grab;
  flex-shrink: 0;
  border-radius: 3px;
  touch-action: none;
}
.block-drag-handle:hover {
  color: #409eff;
  background: rgba(64, 158, 255, 0.08);
}
.block-drag-handle:active {
  cursor: grabbing;
}
.block-ghost {
  opacity: 0.45;
  background: #f5f7fa !important;
  border-style: dashed !important;
}
.block-chosen {
  border-color: #409eff;
  box-shadow: 0 2px 8px rgba(64, 158, 255, 0.2);
}
.block-drag {
  opacity: 0.95;
  cursor: grabbing !important;
}
.block-index {
  font-size: 12px;
  color: #909399;
  width: 18px;
  flex-shrink: 0;
}
.block-type {
  font-size: 13px;
  font-weight: 500;
  flex-shrink: 0;
}
.block-summary {
  font-size: 12px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.block-item-actions {
  flex-shrink: 0;
  white-space: nowrap;
}
.block-item-actions .el-button {
  padding: 4px;
  margin-left: 0;
}
.danger-text {
  color: #f56c6c;
}
.empty-tip {
  padding: 24px 8px;
  text-align: center;
  font-size: 13px;
}
.tip-text {
  font-size: 12px;
  margin: 0 0 8px;
  line-height: 1.5;
}
.var-chips {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}
.var-chip {
  cursor: pointer;
}
.table-rows {
  margin-bottom: 8px;
}
.table-row-edit {
  display: grid;
  grid-template-columns: 1fr 64px 72px 28px;
  gap: 4px;
  margin-bottom: 6px;
  align-items: center;
}
.ticket-wrap {
  display: flex;
  justify-content: center;
  background: #f0f2f5;
  padding: 16px;
  border-radius: 4px;
  overflow-x: auto;
}
.ticket {
  background: #fffef8;
  border: 1px dashed #c0c4cc;
  padding: 12px 10px 20px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  font-family: "Courier New", Consolas, "Sarasa Mono SC", monospace;
}
.ticket-line {
  line-height: 1.35;
  min-height: 1.35em;
}
.ticket-pre {
  margin: 0;
  font-family: inherit;
  font-size: 12px;
  white-space: pre;
  overflow: hidden;
}
.ticket-line.is-bold .ticket-pre {
  font-weight: 700;
}
.ticket-line.is-large .ticket-pre {
  font-size: 14px;
}
.ticket-line.is-fs2 .ticket-pre {
  font-size: 16px;
  font-weight: 700;
}
.qr-box {
  margin: 8px auto;
  width: 72px;
  height: 72px;
  border: 2px solid #303133;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 11px;
  color: #606266;
}
.barcode-box {
  margin: 6px 0;
  text-align: center;
  letter-spacing: -1px;
  font-size: 18px;
  line-height: 1;
  font-weight: 700;
}
.content-panel {
  margin-top: 12px;
}
.content-label {
  font-size: 12px;
  color: #909399;
  margin-bottom: 6px;
}
.content-textarea >>> textarea {
  font-family: Consolas, "Courier New", monospace;
  font-size: 12px;
}

@media (max-width: 991px) {
  .toolbar-actions {
    margin-left: 0;
    width: 100%;
  }
  .panel-card {
    min-height: auto;
  }
  .block-list-header .drag-tip {
    white-space: normal;
    text-align: right;
  }
}
</style>

<!-- forceFallback 拖拽克隆挂到 body，需非 scoped 样式 -->
<style>
.block-drag-fallback {
  display: flex !important;
  align-items: center;
  justify-content: space-between;
  padding: 8px 10px;
  border: 1px solid #409eff;
  border-radius: 4px;
  background: #fff;
  box-shadow: 0 6px 16px rgba(0, 0, 0, 0.12);
  opacity: 0.95;
  cursor: grabbing !important;
  z-index: 9999;
  list-style: none;
}
body.printer-block-dragging {
  cursor: grabbing !important;
  user-select: none;
}
body.printer-block-dragging * {
  cursor: grabbing !important;
}
</style>
